/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseReceivingPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/receiving 朝日荷受け（S3 spec §8.2）— 過磅總重 + 單品確認
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  RBAC gate：operations.weighings.record（PERMISSIONS.receivingManage；
 *  後端 v1 路由實際 gate = logistics.shipments.manage，兩者皆需 — 見路由註記）。
 *  欄位映射：
 *    RecordV12ReceivingWeightDto：receiving_weight_kg（会場總重，字串）/ receiving_measured_at /
 *      variance_explanation / evidence[] / notes。
 *    RecordV12ReceivingDetailsDto：item_details[]（case_item_id / received_item_name /
 *      received_weight_kg / condition_status / condition_notes）。
 *  受付明細需先有出貨計畫（shipment）；無 shipment 時提示先建立出貨。
 */

import { useEffect, useMemo, type ReactElement } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import {
  getCaseShipment,
  recordReceivingDetails,
  recordReceivingWeight,
  type RecordReceivingDetailsDto,
  type RecordReceivingWeightDto,
} from '../api/shipments'
import { getCase } from '../api/cases'
import { ApiError } from '../api/client'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

const decimalString = z
  .string()
  .trim()
  .min(1)
  .refine((v) => Number.isFinite(Number(v)) && Number(v) >= 0, { message: 'non-negative' })

const totalSchema = z.object({
  receivingWeightKg: decimalString,
  receivingMeasuredAt: z.string().trim().min(1),
  notes: z.string().trim().optional().default(''),
})

type TotalFormValues = z.input<typeof totalSchema>

const itemRowSchema = z.object({
  caseItemId: z.number(),
  receivedItemName: z.string().trim().min(1),
  receivedWeightKg: decimalString,
  conditionStatus: z.enum(['as_listed', 'minor_issue', 'major_issue']),
  conditionNotes: z.string().trim().optional().default(''),
})

const detailsSchema = z.object({
  items: z.array(itemRowSchema),
})

type DetailsFormValues = z.input<typeof detailsSchema>

const CONDITION_OPTIONS = [
  { value: 'as_listed', labelKey: 'receiving.condition.asListed' },
  { value: 'minor_issue', labelKey: 'receiving.condition.minor' },
  { value: 'major_issue', labelKey: 'receiving.condition.major' },
] as const

function toIso(value: string): string {
  if (!value) return new Date().toISOString()
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString()
}

export function CaseReceivingPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()

  const shipmentQuery = useQuery({
    queryKey: ['case', caseId, 'shipment'],
    queryFn: () => getCaseShipment(caseId),
    enabled: caseId !== '',
    retry: false,
  })

  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })

  const shipment = shipmentQuery.data?.shipment
  const shipmentId = shipment?.id
  const items = useMemo(() => caseQuery.data?.items ?? [], [caseQuery.data])

  const totalForm = useForm<TotalFormValues>({
    resolver: zodResolver(totalSchema),
    defaultValues: { receivingWeightKg: '', receivingMeasuredAt: '', notes: '' },
  })

  const detailsForm = useForm<DetailsFormValues>({
    resolver: zodResolver(detailsSchema),
    defaultValues: { items: [] },
  })
  const { fields, replace } = useFieldArray({ control: detailsForm.control, name: 'items' })

  // 案件品項載入後，初始化單品荷受け列（received_item_name 預設 = 上架品名）。
  useEffect(() => {
    if (items.length === 0 || fields.length > 0) return
    replace(
      items.map((item) => ({
        caseItemId: item.id,
        receivedItemName: item.item_name,
        receivedWeightKg: '',
        conditionStatus: 'as_listed' as const,
        conditionNotes: '',
      })),
    )
  }, [items, fields.length, replace])

  const weightMutation = useMutation({
    mutationFn: (values: TotalFormValues) => {
      if (shipmentId === undefined) throw new Error('no-shipment')
      const body: RecordReceivingWeightDto = {
        receiving_weight_kg: values.receivingWeightKg.trim(),
        receiving_measured_at: toIso(values.receivingMeasuredAt),
        variance_explanation: undefined,
        evidence: [],
        notes: values.notes?.trim() || undefined,
      }
      return recordReceivingWeight(shipmentId, body)
    },
    onSuccess: () => {
      toast.success(t('receiving.weight.success'))
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'shipment'] })
    },
    onError: (error) => {
      const detail = error instanceof ApiError ? `（${error.message}）` : ''
      toast.error(`${t('receiving.weight.error')}${detail}`)
    },
  })

  const detailsMutation = useMutation({
    mutationFn: (values: DetailsFormValues) => {
      if (shipmentId === undefined) throw new Error('no-shipment')
      const body: RecordReceivingDetailsDto = {
        item_details: values.items.map((row) => ({
          case_item_id: row.caseItemId,
          received_item_name: row.receivedItemName.trim(),
          received_weight_kg: row.receivedWeightKg.trim(),
          condition_status: row.conditionStatus,
          condition_notes: row.conditionNotes?.trim() || undefined,
          revision_reason: undefined,
          variance_explanation: undefined,
          metadata_json: undefined,
        })),
        evidence: [],
        notes: undefined,
      }
      return recordReceivingDetails(shipmentId, body)
    },
    onSuccess: () => {
      toast.success(t('receiving.details.success'))
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'shipment'] })
    },
    onError: (error) => {
      const detail = error instanceof ApiError ? `（${error.message}）` : ''
      toast.error(`${t('receiving.details.error')}${detail}`)
    },
  })

  const onSubmitTotal = totalForm.handleSubmit((values) => weightMutation.mutate(values))
  const onSubmitDetails = detailsForm.handleSubmit((values) => detailsMutation.mutate(values))

  const noShipment =
    !shipmentQuery.isPending &&
    (shipment === undefined ||
      (shipmentQuery.error instanceof ApiError && shipmentQuery.error.status === 404))

  if (shipmentQuery.isPending) {
    return <Skeleton className="h-48 w-full" />
  }

  if (noShipment) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('receiving.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('receiving.noShipment')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('receiving.weight.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(e) => void onSubmitTotal(e)}>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1">
                <Label htmlFor="receivingWeightKg">{t('receiving.field.venueTotalWeight')}</Label>
                <Input
                  id="receivingWeightKg"
                  inputMode="decimal"
                  aria-invalid={!!totalForm.formState.errors.receivingWeightKg}
                  {...totalForm.register('receivingWeightKg')}
                />
                {totalForm.formState.errors.receivingWeightKg ? (
                  <p className="text-xs text-destructive">{t('common.required')}</p>
                ) : null}
              </div>
              <div className="space-y-1">
                <Label htmlFor="receivingMeasuredAt">{t('receiving.field.measuredAt')}</Label>
                <Input
                  id="receivingMeasuredAt"
                  type="datetime-local"
                  aria-invalid={!!totalForm.formState.errors.receivingMeasuredAt}
                  {...totalForm.register('receivingMeasuredAt')}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="receivingNotes">{t('receiving.field.notes')}</Label>
                <Input id="receivingNotes" {...totalForm.register('notes')} />
              </div>
            </div>
            <Button type="submit" disabled={weightMutation.isPending}>
              {weightMutation.isPending
                ? t('receiving.weight.submitting')
                : t('receiving.weight.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {t('receiving.details.title')}（{fields.length}）
          </CardTitle>
        </CardHeader>
        <CardContent>
          {caseQuery.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('receiving.details.empty')}</p>
          ) : (
            <form className="space-y-4" onSubmit={(e) => void onSubmitDetails(e)}>
              <div className="overflow-x-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[12rem]">
                        {t('receiving.field.receivedItemName')}
                      </TableHead>
                      <TableHead className="min-w-[8rem]">
                        {t('receiving.field.receivedWeight')}
                      </TableHead>
                      <TableHead className="min-w-[10rem]">
                        {t('receiving.field.condition')}
                      </TableHead>
                      <TableHead className="min-w-[12rem]">
                        {t('receiving.field.conditionNotes')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fields.map((field, index) => {
                      const rowErrors = detailsForm.formState.errors.items?.[index]
                      return (
                        <TableRow key={field.id}>
                          <TableCell>
                            <Input
                              aria-invalid={!!rowErrors?.receivedItemName}
                              {...detailsForm.register(`items.${index}.receivedItemName`)}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              inputMode="decimal"
                              aria-invalid={!!rowErrors?.receivedWeightKg}
                              {...detailsForm.register(`items.${index}.receivedWeightKg`)}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              control={detailsForm.control}
                              name={`items.${index}.conditionStatus`}
                              render={({ field: ctrl }) => (
                                <Select value={ctrl.value} onValueChange={ctrl.onChange}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {CONDITION_OPTIONS.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {t(opt.labelKey)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Input {...detailsForm.register(`items.${index}.conditionNotes`)} />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
              <Button type="submit" disabled={detailsMutation.isPending}>
                {detailsMutation.isPending
                  ? t('receiving.details.submitting')
                  : t('receiving.details.submit')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
